import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WorkflowContext from "../../context/WorkflowContext";
import styles from "./GeneralInformationDelete.module.scss";
import { GeneralInformationDeleteModel } from "../../../../models/workflow.model";
import { FormikProps, getIn, useFormik } from "formik";
import * as Yup from "yup";
import SpinnerArea from "../../../../components/SpinnerArea";
import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import { useNavigate } from "react-router";
import useService from "../../../../hooks/useService";
import workflowService from "../../../../services/workflow.service";
import MainContext from "../../../../context/mainContext";
import SelectMultiple from "../../../../components/SelectMultiple";
import PlaceholderControl from "../../../../components/PlaceholderControl";
import { DocTypeModel } from "../../../../models/docType.model";
import globalDataService from "../../../../services/globalData.service";
import { DocTypesDialog } from "./GeneralInforamtionDialogs";
import { IUser } from "../../../../models/user.model";
import userService from "../../../../services/users.service";
import IconTooltip from "../../../../components/IconTooltip";

const _emptyGeneralInfo: GeneralInformationDeleteModel = {
  id: 0,
  title: undefined,
  author: null,
  countries: [],
  docType: {
    name: "",
    definition: "",
    examples: "",
    governance: "",
    levelOfApproval: "DepartmentChief",
    id: 0,
    mandatoryPeerReview: false,
    qualityReview: "",
  },
  languages: [],
  coAuthorInternals: [],
  designatedApprovers: [],
  leadingDepartments: [],
  otherDepartmentsInvolved: [],
  leadingDivisions: [],
  otherDivisionsInvolved: [],
  isAssociatedWithProjects: false,
  targetAudience: undefined,
  abstract: undefined,
  answers: [],
  projects: [],
  isCompleted: false,

  isAligned: false,
  notAlignedReason: null,
  topics: [],
  stakeholders: [],
  delegates: [],
  priorityTopics: [],
  canEdit: true,
};

interface FormListData {
  departments: string[];
  divisions: string[];
  docTypes: DocTypeModel[];
  topics: string[];
}

const GeneralInformationDelete: React.FC = () => {
  const { service } = useContext(MainContext);
  const { currentWorkflow, reload, ticketNumber, workflowParentInfo } =
    useContext(WorkflowContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [docTypesDialogShow, docTypesDialogShowSet] = useState(false);

  const [model, loading] = useService(async () => {
    return ticketNumber
      ? await workflowService.generalInfo(ticketNumber)
      : await workflowService.generalInfoDefaultData(
          workflowParentInfo?.ticketNumber
        );
  });

  const [formListData, loadingListData] = useService(async () => {
    const response = await Promise.all([
      globalDataService.departments(),
      globalDataService.divisions(),
      globalDataService.docTypes(),
      globalDataService.topics(),
    ]);
    return {
      departments: response[0],
      divisions: response[1],
      docTypes: response[2],
      topics: response[3],
    } as FormListData;
  });

  const [otherDivisionsOptions, setOtherDivisionsOptions] = useState<string[]>(
    []
  );
  const [otherDepartmentsOptions, setOtherDepartmentsOptions] = useState<
    string[]
  >([]);
  const [levelOfApproval, setLevelOfApproval] = useState<
    "DepartmentChief" | "DivisionChief" | undefined
  >();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [designatedApproversOptions, setDesignatedApproversOptions] = useState<
    IUser[]
  >([]);
  const [fixedDesignatedApprovers, setFixedDesignatedApprovers] = useState<
    IUser[]
  >([]);

  const [showTopics, setShowTopics] = useState(model?.isAligned);

  const validationSchema = Yup.object().shape({
    leadingDivisions: Yup.array()
      .when("docType.id", {
        is: (docTypeId: number) => {
          const docTypeSelected = formListData?.docTypes.find(
            (x) => x.id === docTypeId
          );
          return docTypeSelected?.levelOfApproval === "DivisionChief";
        },
        then: Yup.array().min(1, t("requiredField")!),
        otherwise: Yup.array().optional(),
      })
      .when("docType.id", {
        is: (docTypeId: number) => {
          const docTypeSelected = formListData?.docTypes.find(
            (x) => x.id === docTypeId
          );
          return (
            docTypeSelected?.levelOfApproval === "DivisionChief" &&
            docTypeSelected?.mandatoryPeerReview
          );
        },
        then: Yup.array().max(1, t("maxDivisionValidationMessageForDocType")!),
        otherwise: Yup.array().optional(),
      }),
    leadingDepartments: Yup.array().when("docType.id", {
      is: (docTypeId: number) => {
        const docTypeSelected = formListData?.docTypes.find(
          (x) => x.id === docTypeId
        );
        return docTypeSelected?.levelOfApproval === "DepartmentChief";
      },
      then: Yup.array().min(1, t("requiredField")!),
      otherwise: Yup.array().optional(),
    }),
    designatedApprovers: Yup.array().min(1, t("requiredField")!),
  });

  const formik = useFormik<GeneralInformationDeleteModel>({
    initialValues: model || _emptyGeneralInfo,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const save = (values: GeneralInformationDeleteModel, nextStep: boolean) => {
    service(
      () => {
        values.workflowReason = workflowParentInfo!.workflowReason;
        return workflowService.deletePublication(
          workflowParentInfo!.ticketNumber,
          values,
          nextStep
        );
      },
      {
        callback: (response) => {
          if (!nextStep) setSaveSuccess(true);
          else {
            reload();
            navigate(
              `/workflow/${response.ticketNumber}/${response.nextStep}`,
              { relative: "path" }
            );
          }
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<GeneralInformationDeleteModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  const buildTooltip = (labelKey: string) => (
    <IconTooltip title={t(labelKey)} />
  );

  const optionalLabel = (labelKey: string) => (
    <p>
      {t(labelKey)}
      <em className={styles.optional}>{t("optionalField")}</em>
    </p>
  );

  const getAuthors = (author: IUser | null, coAuthors: IUser[] | null) => {
    const authors = [];

    if (author) {
      authors.push(author.displayName);
    }

    if (coAuthors && coAuthors.length > 0) {
      for (const coAuthor of coAuthors) {
        if (coAuthor && coAuthor.displayName) {
          authors.push(coAuthor.displayName);
        }
      }
    }

    return authors.join(", ");
  };

  const onLeadingDivisionsChange = (
    formik: FormikProps<GeneralInformationDeleteModel>,
    newValues: string[]
  ) => {
    formik.setFieldValue("leadingDivisions", newValues);

    const otherDivisionsFiltered =
      formik.values.otherDivisionsInvolved.filter(
        (x) => !newValues.includes(x)
      ) || [];
    if (
      otherDivisionsFiltered.toString() !==
      formik.values.otherDivisionsInvolved.toString()
    )
      formik.setFieldValue("otherDivisionsInvolved", otherDivisionsFiltered);

    const filteredOptions =
      formListData?.divisions.filter((x) => !newValues.includes(x)) || [];
    setOtherDivisionsOptions([...filteredOptions]);

    setSelectedDivisions([...newValues, ...otherDivisionsFiltered]);
  };

  const onLeadingDeparmentsChange = (
    formik: FormikProps<GeneralInformationDeleteModel>,
    newValues: string[]
  ) => {
    formik.setFieldValue("leadingDepartments", newValues);

    const otherDepartmentsFiltered =
      formik.values.otherDepartmentsInvolved.filter(
        (x) => !newValues.includes(x)
      ) || [];

    if (
      otherDepartmentsFiltered.toString() !==
      formik.values.otherDepartmentsInvolved.toString()
    ) {
      formik.setFieldValue("otherDepartmentsInvolved", [
        ...otherDepartmentsFiltered,
      ]);
    }

    const filteredOptions =
      formListData?.departments.filter((x) => !newValues.includes(x)) || [];
    setOtherDepartmentsOptions([...filteredOptions]);

    setSelectedDepartments([...newValues, ...otherDepartmentsFiltered]);
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);

      setShowTopics(model.isAligned);

      if (model.docType.id > 0) {
        setLevelOfApproval(model.docType.levelOfApproval);
      }

      setOtherDepartmentsOptions([
        ...(formListData?.departments.filter(
          (x) => !model.leadingDepartments.includes(x)
        ) || []),
      ]);
      setSelectedDepartments([
        ...model.leadingDepartments,
        ...model.otherDepartmentsInvolved,
      ]);

      setOtherDivisionsOptions([
        ...(formListData?.divisions.filter(
          (x) => !model.leadingDivisions.includes(x)
        ) || []),
      ]);
      setSelectedDivisions([
        ...model.leadingDivisions,
        ...model.otherDivisionsInvolved,
      ]);
    }
  }, [model, formListData]);

  useEffect(() => {
    let active = true;

    const searchChiefs = async () => {
      if (active && model && levelOfApproval) {
        service(
          () =>
            levelOfApproval === "DepartmentChief"
              ? userService.departmentChiefs(selectedDepartments)
              : userService.divisionChiefs(selectedDivisions),
          {
            callback: (data) => {
              setDesignatedApproversOptions(data);

              const filteredApprovers =
                formik.values.designatedApprovers?.filter((x) =>
                  data.some(
                    (c) => x.email.toLowerCase() === c.email.toLowerCase()
                  )
                ) || [];

              formik.setFieldValue("designatedApprovers", [
                ...filteredApprovers,
              ]);
              active = false;
            },
          }
        );
      }
    };

    searchChiefs();

    return () => {
      active = false;
    };
  }, [model, levelOfApproval, selectedDepartments, selectedDivisions]);

  useEffect(() => {
    const fixedOptions =
      levelOfApproval === "DepartmentChief"
        ? designatedApproversOptions.filter((x) => {
            const department = x.department!.split("/")[0];
            return formik.values.leadingDepartments.includes(department);
          })
        : designatedApproversOptions.filter((x) =>
            formik.values.leadingDivisions.includes(x.department!)
          );

    setFixedDesignatedApprovers(fixedOptions);
  }, [
    designatedApproversOptions,
    formik.values.leadingDepartments,
    formik.values.leadingDivisions,
  ]);

  return (
    <SpinnerArea loading={loading || loadingListData || saving}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.generalInformationForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber ??
                  workflowParentInfo?.ticketNumber}
              </p>
              <p>
                <strong>{t("generalInformation")}</strong>
                <em>{t("generalInformationTooltip")}</em>
              </p>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formContainer}>
          <div className={styles["area-title"]}>
            <FormControl fullWidth>
              <FormLabel id="title-label">{t("title")}</FormLabel>
              <TextField
                id="title"
                name="title"
                placeholder={t("title") || ""}
                fullWidth
                inputProps={{
                  readOnly: true,
                }}
                value={formik.values.title || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={true}
                helperText={formik.touched.title && formik.errors.title}
                error={formik.touched.title && Boolean(formik.errors.title)}
              />
            </FormControl>
          </div>
          <div className={styles["area-author"]}>
            <FormControl fullWidth>
              <FormLabel id="author-label">
                {t("authors")}
                {buildTooltip("authorTooltip")}
              </FormLabel>

              <TextField
                id="author"
                name="author"
                placeholder={t("authors") || ""}
                fullWidth
                inputProps={{
                  readOnly: true,
                }}
                value={getAuthors(
                  formik.values.author,
                  model?.coAuthorInternals || null
                )}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={true}
                helperText={formik.touched.author && formik.errors.author}
                error={formik.touched.author && Boolean(formik.errors.author)}
              />
            </FormControl>
          </div>
          <div className={styles["area-topics"]}>
            <FormControl fullWidth>
              <FormLabel id="topics-label">{t("topicsLabel")}</FormLabel>
              <TextField
                id="topics"
                name="topics"
                placeholder={t("topics") || ""}
                fullWidth
                inputProps={{
                  readOnly: true,
                }}
                value={formik.values.topics || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={true}
                helperText={formik.touched.topics && formik.errors.topics}
                error={formik.touched.topics && Boolean(formik.errors.topics)}
              />
            </FormControl>
          </div>
          <div className={styles["area-doc-type"]}>
            <FormControl fullWidth>
              <FormLabel id="docType-label">
                {t("docType")}{" "}
                <Button
                  variant="text"
                  onClick={() => docTypesDialogShowSet(true)}
                >
                  {t("viewDocTypeExamples")}
                </Button>
              </FormLabel>
              <Select
                id="docType.id"
                name="docType.id"
                disabled={Boolean(workflowParentInfo)}
                className={
                  getIn(formik.touched, "docType.id") &&
                  getIn(formik.errors, "docType.id")
                    ? "Mui-error"
                    : ""
                }
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.docType.id.toString()}
                onChange={(event: SelectChangeEvent) => {
                  formik.setFieldValue("docType.id", event.target.value);

                  setTimeout(() => {
                    const docTypeSelected = formListData?.docTypes.find(
                      (x) => x.id === parseInt(event.target.value)
                    )!;
                    setLevelOfApproval(docTypeSelected.levelOfApproval);
                    switch (docTypeSelected.levelOfApproval) {
                      case "DepartmentChief":
                        formik.setFieldValue("leadingDivisions", []);
                        formik.setFieldValue("otherDivisionsInvolved", []);
                        formik.setFieldValue("designatedApprovers", []);
                        setSelectedDivisions([]);
                        break;
                      case "DivisionChief":
                        formik.setFieldValue("leadingDepartments", []);
                        formik.setFieldValue("otherDepartmentsInvolved", []);
                        formik.setFieldValue("designatedApprovers", []);
                        setSelectedDepartments([]);
                        break;
                    }
                  }, 500);
                }}
                onBlur={formik.handleBlur}
                displayEmpty
                renderValue={(value) =>
                  value !== "0" ? (
                    formListData?.docTypes.find((x) => x.id === parseInt(value))
                      ?.name
                  ) : (
                    <PlaceholderControl text={t("docType")} />
                  )
                }
              >
                <MenuItem
                  key={`doc_types_${0}`}
                  value={0}
                  className={"selectPlaceholder"}
                >
                  {t("docType")}
                </MenuItem>
                {formListData?.docTypes.map((x) => (
                  <MenuItem key={`doc_types_${x.id}`} value={x.id!}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="Mui-error">
                {getIn(formik.touched, "docType.id") &&
                  getIn(formik.errors, "docType.id")}
              </FormHelperText>
            </FormControl>
          </div>
          <div className={styles["area-leading-divisions"]}>
            <FormControl fullWidth>
              <FormLabel id="leadingDivisions-label">
                {t("leadingDivisions")}{" "}
                {buildTooltip("leadingDivisionsTooltip")}
              </FormLabel>
              <SelectMultiple
                id="leadingDivisions"
                placeholder={t("selectLeadingDivisions").toString()}
                currentValues={formik.values.leadingDivisions || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.divisions || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  onLeadingDivisionsChange(formik, newValues)
                }
                disabled={levelOfApproval === "DepartmentChief"}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.leadingDivisions &&
                  formik.errors.leadingDivisions
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-others-divisions"]}>
            <FormControl fullWidth>
              <FormLabel id="otherDivisionsInvolved-label">
                {optionalLabel("otherDivisionsInvolved")}
                {buildTooltip("otherDivisionsInvolvedTooltip")}
              </FormLabel>
              <SelectMultiple
                id="otherDivisionsInvolved"
                placeholder={t("selectOtherDivisionsInvolved").toString()}
                currentValues={formik.values.otherDivisionsInvolved || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={otherDivisionsOptions}
                readOnly={isReadOnly}
                onBlur={formik.handleBlur}
                onChange={(newValues) => {
                  formik.setFieldValue("otherDivisionsInvolved", [
                    ...newValues,
                  ]);
                  setSelectedDivisions([
                    ...formik.values.leadingDivisions,
                    ...newValues,
                  ]);
                }}
                disabled={levelOfApproval === "DepartmentChief"}
              />
            </FormControl>
          </div>
          <div className={styles["area-leading-departments"]}>
            <FormControl fullWidth>
              <FormLabel id="leadingDeparments-label">
                {t("leadingDepartments")}{" "}
                {buildTooltip("leadingDepartmentsTooltip")}
              </FormLabel>
              <SelectMultiple
                id="leadingDepartments"
                placeholder={t("selectLeadingDepartments").toString()}
                currentValues={formik.values.leadingDepartments || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.departments || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  onLeadingDeparmentsChange(formik, newValues)
                }
                disabled={levelOfApproval === "DivisionChief"}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.leadingDepartments &&
                  formik.errors.leadingDepartments
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-others-departments"]}>
            <FormControl fullWidth>
              <FormLabel id="otherDepartmentsInvolved-label">
                {optionalLabel("otherDepartmentsInvolved")}
                {buildTooltip("otherDepartmentsInvolvedTooltip")}
              </FormLabel>
              <SelectMultiple
                id="otherDepartmentsInvolved"
                placeholder={t("selectOtherDepartmentsInvolved").toString()}
                currentValues={formik.values.otherDepartmentsInvolved || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={otherDepartmentsOptions}
                readOnly={isReadOnly}
                onChange={(newValues) => {
                  formik.setFieldValue("otherDepartmentsInvolved", newValues);
                  setSelectedDepartments([
                    ...formik.values.leadingDepartments,
                    ...newValues,
                  ]);
                }}
                disabled={levelOfApproval === "DivisionChief"}
                onBlur={formik.handleBlur}
              />
            </FormControl>
          </div>
          <div className={styles["area-designated-approver"]}>
            <FormControl fullWidth>
              <FormLabel id="designatedApprovers-label">
                {t("designatedApprovers")}{" "}
                {buildTooltip("designatedApproversTooltip")}
              </FormLabel>
              <SelectMultiple
                id="designatedApprovers"
                fixedValues={fixedDesignatedApprovers}
                placeholder={t("selectDesignatedApprovers").toString()}
                currentValues={[...formik.values.designatedApprovers!] || []}
                keySelector={(x) => x.email}
                labelSelector={(x) =>
                  t(
                    levelOfApproval === "DepartmentChief"
                      ? "designatedApproverDepartment"
                      : "designatedApproverDivision",
                    { department: x.department, displayName: x.displayName }
                  )
                }
                source={designatedApproversOptions}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  formik.setFieldValue("designatedApprovers", newValues)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.designatedApprovers &&
                  formik.errors.designatedApprovers
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-abstract"]}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                <p>{t("briefDescription")}</p>
              </FormLabel>
              <TextField
                id="abstract"
                name="abstract"
                placeholder={t("description") || ""}
                fullWidth
                rows={3}
                multiline
                inputProps={{ readOnly: true }}
                value={formik.values.abstract || ""}
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.abstract && formik.errors.abstract}
                error={
                  formik.touched.abstract && Boolean(formik.errors.abstract)
                }
              />
            </FormControl>
          </div>
        </div>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.continue}
            disabled={
              ((!formik.dirty || !formik.isValid) && model?.id === 0) ||
              isReadOnly ||
              !formik.isValid
            }
            onClick={() => save(formik.values, true)}
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
      <DocTypesDialog
        docTypes={formListData?.docTypes}
        open={docTypesDialogShow}
        setOpen={docTypesDialogShowSet}
      />
    </SpinnerArea>
  );
};

export default GeneralInformationDelete;
